import * as React from 'react'

import * as Progress from '@radix-ui/react-progress'
import { animate, useMotionValue, useTransform } from 'motion/react'
import * as m from 'motion/react-m'

import { Badge, Flex, Label, Text } from '@buffer-mono/popcorn'

import { getScoreColors } from '../../util/commentScoreTheme'
import { EASE_IN_OUT } from '../../util/easingRules'

import styles from './CommentScoreMetric.module.css'

import type { JSX } from 'react'

export const CommentScoreMetric = ({
  level,
  score,
}: {
  level: string
  score: number
}): JSX.Element => {
  const progress = useMotionValue(0)
  const roundedProgress = useTransform(() => Math.round(progress.get()))
  const pathLength = useTransform(progress, [0, 100], [0, 1])
  const filter = useTransform(progress, [0, score], ['blur(10px)', 'blur(0px)'])
  const scale = useTransform(progress, [0, score], [0.5, 1])
  const opacity = useTransform(progress, [0, score], [0.5, 1])

  const maxDuration = 2 // seconds for 100
  const minDuration = 0.5 // minimum duration for very low values
  const duration = Math.max((score / 100) * maxDuration, minDuration)
  const colors = getScoreColors(score, 'neutral')

  React.useEffect(() => {
    if (score === 0) {
      progress.set(0) // No arc, just the dot
    } else {
      animate(progress, score, {
        duration,
        ease: EASE_IN_OUT.cubic,
      })
    }
  }, [progress, duration, score])

  return (
    <Flex direction="column" align="center" gap="xs">
      <Badge
        className={styles.scoreBadge}
        size="medium"
        style={{
          backgroundColor: colors.bg,
          color: colors.textColor,
          borderColor: colors.progressColor,
        }}
      >
        <Label size="medium" className={styles.scoreLabel}>
          {level}
        </Label>
      </Badge>
      <div className={styles.container}>
        <Progress.Root className={styles.root}>
          <Progress.Indicator asChild>
            <div className={styles.indicatorContainer}>
              <svg className={styles.indicator} viewBox="0 0 100 100">
                {/* Gauge arc: radius 42 centered at (50,50) so it sits
                    concentric with the score circle, leaving an even gap.
                    ~85° opening at the bottom for the "/100" caption. */}
                {/* Base arc (background) */}
                <path
                  d="M21.6 80.9 A42 42 0 1 1 78.4 80.9"
                  fill="none"
                  stroke={colors.progressBg}
                  strokeWidth="8.5"
                  strokeLinecap="round"
                />
                {/* Progress arc (foreground, animated) or left dot for 0 */}
                {score === 0 ? (
                  <circle
                    cx={21.6}
                    cy={80.9}
                    r={4.25}
                    fill={colors.progressColor}
                  />
                ) : (
                  <m.path
                    d="M21.6 80.9 A42 42 0 1 1 78.4 80.9"
                    fill="none"
                    stroke={colors.progressColor}
                    strokeWidth="8.5"
                    style={{
                      pathLength,
                      strokeLinecap: 'round',
                    }}
                  />
                )}
              </svg>
              <div className={styles.inputContainer}>
                <div
                  className={styles.inputWrapper}
                  style={{
                    backgroundColor: colors.bg,
                  }}
                >
                  <m.div
                    className={styles.input}
                    style={{
                      filter,
                      scale,
                      opacity,
                      color: colors.textColor,
                      willChange: 'filter, transform, opacity',
                    }}
                  >
                    {roundedProgress}
                  </m.div>
                </div>
                <Text
                  size="sm"
                  color="subtle"
                  weight="medium"
                  className={styles.scoreMax}
                >
                  /100
                </Text>
              </div>
            </div>
          </Progress.Indicator>
        </Progress.Root>
      </div>
    </Flex>
  )
}
